import apiClient from '@/shared/api/client';
import { ApiResponse } from '@/shared/api/types';

/** A business that showed up in a check. */
export interface RankCompetitor {
  name: string | null;
  domain: string | null;
  position: number;
}

/** One surface of one search term, as the backend measured it this week. */
export interface RankCell {
  /** 1-based; null = not found within the table's `depth`. */
  rank: number | null;
  appeared: boolean;
  /** Last week's check of the same surface; null when there was none to compare with. */
  previous: { rank: number | null } | null;
  /** The practice page that ranked (Google web results only). */
  url: string | null;
  /** Who ranked above the practice here, or the top results when it was not found. */
  ahead: RankCompetitor[];
  /** The top businesses that were not the practice on this surface (a few at most). */
  competitors: RankCompetitor[];
  /** The exact text searched or asked this week, city included. */
  queries: string[];
  /** Weekly history, oldest first, for the weeks this surface was checked. */
  trend: { weekOf: string; rank: number | null }[];
  checkedAt: string;
}

/** The local-results cell: the practice's place in the list opened from the map pack. */
export interface LocalRankCell extends RankCell {
  /** Its place within the 3-place map pack; null when it was not in the pack. */
  packRank: number | null;
}

export interface RankTableRow {
  term: string;
  google_organic: RankCell | null;
  google_local_finder: LocalRankCell | null;
  chatgpt: RankCell | null;
}

export interface RankTable {
  weekOf: string;
  /** How far down each Google check looked; "not found" means not in the first `depth`. */
  depth: number;
  /** The oldest check shown, as ms since epoch; null when nothing has been checked yet. */
  observedAt: number | null;
  rows: RankTableRow[];
}

export const rankingsApi = {
  get: async (locationId: string): Promise<RankTable> => {
    const res = await apiClient.get<ApiResponse<RankTable>>('/seo/rank-table', {
      params: { locationId },
    });
    return res.data.data;
  },
};
